import { useState } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { MessageSquare, Phone, Plus, Users } from "lucide-react";
import { contactPolicy } from "@shared/contactPolicy";
import { errorMessage } from "@web/lib/api";
import { LEAD_STATUS_LABEL, LEAD_STATUS_ORDER, fmtAgo, leadStatusTone, leadTitle } from "@web/lib/formatLeads";
import { useNavAction } from "@web/lib/navAction";
import { useDebounced } from "@web/lib/useDebounced";
import { statusTone } from "@web/lib/format";
import { ACTIVE_LEAD_STATUSES, leadsQuery, type LeadListItem, type LeadsFilter } from "@web/lib/queries-leads";
import { useBatchValidation, type RecordEvaluation } from "@web/lib/validation";
import { Button, ChipStrip, CollectionHeader, EmptyState, LeadCard, NavBarAction, Page, RowGroup, SearchBar, SegmentedControl, SelectChip, Skeleton, Stack, StatusChip, Text, Toolbar, ValidationBadge } from "@web/ui";
import { CallSheet, TextSheet, type CommsTarget } from "./lead/CommsSheets";
import { NewLeadSheet } from "./sheets/NewLeadSheet";

const SORTS = [
  { key: "activity", label: "Last activity" },
  { key: "next_action", label: "Next action" },
  { key: "event_date", label: "Event date" },
] as const;
type Sort = (typeof SORTS)[number]["key"];
export type LeadsSearch = { sort?: Sort; status?: string; q?: string };

/** The shareable address contract: unknown keys and oversized values are dropped,
 * and an absent key means the screen's own default. */
export function parseLeadsSearch(raw: Record<string, unknown>): LeadsSearch {
  const out: LeadsSearch = {};
  if (SORTS.some(s => s.key === raw.sort)) out.sort = raw.sort as Sort;
  if (typeof raw.status === "string" && raw.status && (raw.status === "active" || LEAD_STATUS_ORDER.includes(raw.status as (typeof LEAD_STATUS_ORDER)[number]))) out.status = raw.status;
  if (typeof raw.q === "string" && raw.q && raw.q.length <= 200) out.q = raw.q;
  return out;
}

const targetOf = (l: LeadListItem): CommsTarget => ({ leadId: l.id, contactId: l.contactId, name: leadTitle(l), phone: l.contactPhone, email: l.contactEmail, smsConsent: l.smsConsent ?? 0, emailOptOut: l.emailOptOut ?? 1, dnc: l.contactDnc ?? 1 });

/** Leads (SPEC §6.1 item 3): status chips (Active by default), search, sort, rows with call / text quick actions; `+` opens the New lead sheet. */
export function Leads() {
  const navigate = useNavigate();
  // Status, sort and search live in the URL, so Back from a lead returns to the
  // list the person built and the address is the view they are looking at
  // (docs/plans/RESPONSIVE-STANDARD.md).
  const search = useSearch({ from: "/app/leads" });
  const status = search.status ?? "active";
  const sort = search.sort ?? "activity";
  const q = search.q ?? "";
  const setSearch = (next: LeadsSearch) => navigate({ to: "/leads", search: { ...search, ...next }, replace: true });
  const [adding, setAdding] = useState(false);
  const [sheet, setSheet] = useState<{ kind: "text" | "call"; target: CommsTarget } | null>(null);
  useNavAction({ label: "New lead", icon: <Plus aria-hidden size={26} />, onClick: () => setAdding(true) });
  const debounced = useDebounced(q);
  const filter: LeadsFilter = { q: debounced || undefined, status: status === "active" ? ACTIVE_LEAD_STATUSES : status, sort };
  const list = useInfiniteQuery(leadsQuery(filter));
  const rows = list.data?.pages.flatMap((p) => p.items) ?? [];
  const checks = useBatchValidation(rows.map(row => ({ entityType: "lead", id: row.id })));

  const quick = (l: LeadListItem, kind: "text" | "call") => {
    if (!contactPolicy(targetOf(l), kind === "text" ? "sms" : "call").allowed) return;
    if (kind === "call" && l.contactPhone) window.location.assign(`tel:${l.contactPhone}`);
    setSheet({ kind, target: targetOf(l) });
  };

  return (
    <Page variant="collection">
      <Stack gap={3}>
        <Toolbar>
          <SearchBar value={q} onChange={(value) => setSearch({ q: value || undefined })} placeholder="Search leads" label="Search leads" />
          <SegmentedControl label="Sort" items={SORTS} value={sort} onChange={(value) => setSearch({ sort: value })} />
        </Toolbar>
        <ChipStrip label="Status" wrap>
          <SelectChip selected={status === "active"} onClick={() => setSearch({ status: undefined })}>
            Active
          </SelectChip>
          {LEAD_STATUS_ORDER.map((s) => (
            <SelectChip key={s} selected={status === s} onClick={() => setSearch({ status: s })}>
              {LEAD_STATUS_LABEL[s]}
            </SelectChip>
          ))}
        </ChipStrip>
        {list.isError ? (
          <EmptyState icon={Users} title="Leads unavailable" body={errorMessage(list.error)} action={<Button onClick={() => void list.refetch()}>Retry leads</Button>} />
        ) : list.isLoading && !list.data ? (
          <Skeleton count={6} />
        ) : rows.length === 0 ? (
          <EmptyState icon={Users} title={debounced ? "No matches" : "No leads here"} body={debounced ? "Try a name, phone or email." : "New leads from your websites and the + button will appear here."} />
        ) : (
          <RowGroup>
            <CollectionHeader columns={["Contact / event", "Status / activity"]} />
            {rows.map((l) => (
              <LeadRow key={l.id} l={l} validation={checks.data?.records.find(record => record.entity.id === l.id)} unavailable={checks.isError} onOpen={() => navigate({ to: "/leads/$id", params: { id: l.id } })} onCall={() => quick(l, "call")} onText={() => quick(l, "text")} />
            ))}
          </RowGroup>
        )}
        {list.hasNextPage && (
          <Button variant="secondary" loading={list.isFetchingNextPage} onClick={() => list.fetchNextPage()}>
            Load more
          </Button>
        )}
      </Stack>
      {adding && <NewLeadSheet onClose={() => setAdding(false)} />}
      {sheet?.kind === "text" && <TextSheet target={sheet.target} onClose={() => setSheet(null)} />}
      {sheet?.kind === "call" && <CallSheet target={sheet.target} onClose={() => setSheet(null)} />}
    </Page>
  );
}

function LeadRow({ l, onOpen, onCall, onText, validation, unavailable }: { l: LeadListItem; onOpen: () => void; onCall: () => void; onText: () => void; validation?: RecordEvaluation; unavailable?: boolean }) {
  const chip = <StatusChip tone={l.bookingStatus ? statusTone(l.bookingStatus) : leadStatusTone(l.status)}>{l.card.status}</StatusChip>;
  const sms = contactPolicy(targetOf(l), "sms"), call = contactPolicy(targetOf(l), "call");
  return (
    <LeadCard
      card={l.card} status={l.status} venueColor={l.venueColor}
      detail={sms.reason ?? undefined}
      trailing={
        <Stack gap={1} align="end">
          {chip}
          <ValidationBadge result={validation} unavailable={unavailable} />
          <Text as="span" variant="caption" tone="tertiary">
            {fmtAgo(l.lastActivityAt)}
          </Text>
        </Stack>
      }
      onClick={onOpen}
      actions={
        <>
          <NavBarAction label={`Call ${leadTitle(l)}`} icon={<Phone aria-hidden size={20} />} disabled={!call.allowed} disabledReason={call.reason} onClick={onCall} />
          <NavBarAction label={`Text ${leadTitle(l)}`} icon={<MessageSquare aria-hidden size={20} />} disabled={!sms.allowed} disabledReason={sms.reason} onClick={onText} />
        </>
      }
    />
  );
}
